/** Per-channel publish state stored on the manual product (`channels.{shopId}`). */

export const STUCK_AFTER_MS = 10 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
// Three ASCII periods (U+002E). Not the ellipsis character.
export const PUBLISHING_MARK = "...";

export function publishingChipText(name) {
  return `Publishing to ${name || "your sales channel"}${PUBLISHING_MARK}`;
}

export function channelName(entry, channel) {
  return (
    entry?.name ||
    channel?.name ||
    channel?.platformName ||
    "your sales channel"
  );
}

export function normalizeChannelStatus(status) {
  if (status === "published") return "live";
  return status || null;
}

/**
 * One row per shop. `channels` wins. Shops that are already linked and have
 * no in-progress row show as live. A pending row stays pending even if an
 * older activeShops entry is still on the product.
 */
export function channelPublishEntries(product, keyedChannels = {}) {
  const rows = [];
  const seen = new Set();
  const channels = product?.channels;
  const legacy = !channels || typeof channels !== "object" || Array.isArray(channels);

  if (!legacy) {
    Object.entries(channels).forEach(([shopId, raw]) => {
      if (!raw) return;
      const status = normalizeChannelStatus(
        typeof raw === "string" ? raw : raw.status
      );
      if (!status) return;
      const channel = keyedChannels?.[shopId];
      rows.push({
        shopId,
        status,
        name: channelName(typeof raw === "object" ? raw : null, channel),
        startedAt: typeof raw === "object" ? raw.startedAt : null,
        updatedAt: typeof raw === "object" ? raw.updatedAt : null,
        error: typeof raw === "object" ? raw.error : null,
        listingUrl: typeof raw === "object" ? raw.listingUrl : null,
        submit: typeof raw === "object" ? raw.submit : null,
      });
      seen.add(String(shopId));
    });
  } else {
    const statusMap = product?.channelStatus || {};
    const errors = product?.channelStatusError || {};
    Object.entries(statusMap).forEach(([shopId, status]) => {
      const normalized = normalizeChannelStatus(status);
      if (!normalized) return;
      const channel = keyedChannels?.[shopId];
      rows.push({
        shopId,
        status: normalized,
        name: channelName(null, channel),
        error: errors?.[shopId] || null,
        startedAt: null,
        listingUrl: null,
        submit: null,
      });
      seen.add(String(shopId));
    });
  }

  (product?.activeShops || []).forEach((id) => {
    if (seen.has(String(id))) return;
    const channel = keyedChannels?.[id];
    rows.push({
      shopId: id,
      status: "live",
      name: channelName(null, channel),
      listingUrl: null,
      error: null,
      submit: null,
    });
  });

  return rows;
}

export function publishingCopy(entry, now = Date.now()) {
  const name = entry?.name || "your sales channel";
  const started = Number(entry?.startedAt || entry?.updatedAt);
  if (!started || now - started < MINUTE_MS) {
    return { text: publishingChipText(name), stuck: false };
  }
  const mins = Math.max(1, Math.round((now - started) / MINUTE_MS));
  return {
    text: `Still publishing (${mins} min)`,
    stuck: now - started >= STUCK_AFTER_MS,
  };
}

export function retryPayload(product, entry) {
  if (entry?.submit && entry.submit.shopId) return entry.submit;
  return {
    manualProducts: [
      {
        id: product?.id,
        taxanomyId: product?.metaData?.taxanomyId,
        shippingTemplateId: product?.metaData?.shippingTemplateId,
        attributionSellerId:
          product?.attributionByShop?.[entry.shopId] ||
          product?.attributionSellerId,
        channelCategoryId:
          product?.channelCategoryByShop?.[entry.shopId] ||
          product?.channelCategoryId,
      },
    ],
    shopId: entry?.shopId,
    attributionSellerId:
      product?.attributionByShop?.[entry.shopId] ||
      product?.attributionSellerId,
    channelCategoryId:
      product?.channelCategoryByShop?.[entry.shopId] ||
      product?.channelCategoryId,
  };
}

const seenStatus = new Map();

/** Remember a status we've already shown, so the first snapshot does not toast. */
export function seedChannelStatus(productId, channels, { force = false } = {}) {
  if (!productId || !channels || typeof channels !== "object") return;
  Object.entries(channels).forEach(([shopId, raw]) => {
    const status = normalizeChannelStatus(
      typeof raw === "string" ? raw : raw?.status
    );
    if (!status) return;
    const key = `${productId}:${shopId}`;
    if (force || !seenStatus.has(key)) seenStatus.set(key, status);
  });
}

/** Toast only on pending → live, and only the first time that flip is observed. */
export function observeChannelFlip(productId, channels, onLive) {
  if (!productId || !channels || typeof channels !== "object") return;
  Object.entries(channels).forEach(([shopId, raw]) => {
    const status = normalizeChannelStatus(
      typeof raw === "string" ? raw : raw?.status
    );
    if (!status) return;
    const key = `${productId}:${shopId}`;
    const prev = seenStatus.get(key);
    seenStatus.set(key, status);
    if (prev === "pending" && status === "live" && onLive) {
      onLive({
        shopId,
        name: (typeof raw === "object" && raw?.name) || "your sales channel",
        listingUrl: (typeof raw === "object" && raw?.listingUrl) || null,
      });
    }
  });
}

export function publishingNames(channels) {
  if (!channels || typeof channels !== "object") return [];
  return Object.values(channels)
    .filter((entry) => normalizeChannelStatus(entry?.status) === "pending")
    .map((entry) => entry?.name || "your sales channel");
}
